import { useState, useEffect, useCallback } from 'react';
import { get, post } from '../api';
import { nowTimeInTz } from '../dateUtils';

interface Recipe {
  id: number;
  name: string;
  category: string | null;
  kcal: number | null;
  protein_g: number | null;
  fat_g: number | null;
  carbs_g: number | null;
  image_url: string | null;
  source?: string;
}

interface Product {
  id: number;
  name: string;
  brand: string | null;
  portion_size_g: number | null;
  portion_label: string | null;
  kcal_per_100: number | null;
  protein_g_per_100: number | null;
  fat_g_per_100: number | null;
  carbs_g_per_100: number | null;
}

interface FixedMeal {
  id: number;
  name: string;
  total_kcal_per_day: number | null;
  total_protein_g_per_day: number | null;
  total_fat_g_per_day: number | null;
  total_carbs_g_per_day: number | null;
  times_per_day: number;
  active: boolean;
}

type Tab = 'recipe' | 'product' | 'daily' | 'manual';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const MEAL_TYPES = [
  { value: 'breakfast', label: 'Frühstück' },
  { value: 'lunch', label: 'Mittagessen' },
  { value: 'dinner', label: 'Abendessen' },
  { value: 'snack', label: 'Snack' },
  { value: 'other', label: 'Sonstiges' },
];

export default function LogMealModal({ open, onClose, onSaved }: Props) {
  const [tab, setTab] = useState<Tab>('recipe');
  const [saving, setSaving] = useState(false);

  // Recipe tab
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [recipeSearch, setRecipeSearch] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [recipeServings, setRecipeServings] = useState(1);

  // Product tab
  const [products, setProducts] = useState<Product[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productServings, setProductServings] = useState(1);
  const [productAmountMode, setProductAmountMode] = useState<'portions' | 'grams'>('portions');
  const [productGrams, setProductGrams] = useState('');

  // Daily meal tab
  const [fixedMeals, setFixedMeals] = useState<FixedMeal[]>([]);
  const [dailyMultiplier, setDailyMultiplier] = useState<Record<number, number>>({});

  // Manual tab
  const [manualDesc, setManualDesc] = useState('');
  const [manualType, setManualType] = useState('snack');
  const [manualKcal, setManualKcal] = useState('');
  const [manualProtein, setManualProtein] = useState('');
  const [manualFat, setManualFat] = useState('');
  const [manualCarbs, setManualCarbs] = useState('');

  // Today's plan recipe IDs for sorting
  const [todayRecipeIds, setTodayRecipeIds] = useState<Set<number>>(new Set());

  // Load data
  useEffect(() => {
    if (!open) return;
    Promise.all([
      get<Recipe[]>('/recipes'),
      get<Product[]>('/food-items'),
      get<FixedMeal[]>('/daily-fixed-meals'),
      get<{ today_plan?: { meals?: { recipe_id?: number }[] } }>('/dashboard'),
    ]).then(([r, p, fm, dash]) => {
      // Extract today's plan recipe IDs
      const planIds = new Set<number>();
      if (dash.today_plan?.meals) {
        for (const m of dash.today_plan.meals) {
          if (m.recipe_id) planIds.add(m.recipe_id);
        }
      }
      setTodayRecipeIds(planIds);
      // Sort: today's plan recipes first
      r.sort((a, b) => {
        const aInPlan = planIds.has(a.id) ? 1 : 0;
        const bInPlan = planIds.has(b.id) ? 1 : 0;
        return bInPlan - aInPlan;
      });
      setRecipes(r);
      setProducts(p);
      setFixedMeals(fm.filter((m: FixedMeal & { active: number | boolean }) => m.active));
    }).catch(() => {});
  }, [open]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setSelectedRecipe(null);
      setSelectedProduct(null);
      setRecipeSearch('');
      setProductSearch('');
      setRecipeServings(1);
      setProductServings(1);
      setProductAmountMode('portions');
      setProductGrams('');
      setDailyMultiplier({});
      setManualDesc('');
      setManualKcal('');
      setManualProtein('');
      setManualFat('');
      setManualCarbs('');
    }
  }, [open]);

  const now = () => nowTimeInTz();

  const doSave = useCallback(async (body: Record<string, unknown>) => {
    setSaving(true);
    try {
      await post('/tracking', { time: now(), ...body });
      onSaved();
      onClose();
    } catch { /* ignore */ }
    finally { setSaving(false); }
  }, [onSaved, onClose]);

  const saveRecipe = () => {
    if (!selectedRecipe) return;
    doSave({
      meal_type: 'meal',
      recipe_id: selectedRecipe.id,
      servings: recipeServings,
    });
  };

  const saveProduct = () => {
    if (!selectedProduct) return;
    if (productAmountMode === 'grams' && productGrams) {
      doSave({
        meal_type: 'snack',
        product_id: selectedProduct.id,
        amount_g: Number(productGrams),
      });
    } else {
      doSave({
        meal_type: 'snack',
        product_id: selectedProduct.id,
        servings: productServings,
      });
    }
  };

  const saveDailyMeal = (meal: FixedMeal) => {
    const mult = dailyMultiplier[meal.id] ?? 1;
    doSave({
      meal_type: 'daily_meal',
      description: `${meal.name}${mult !== 1 ? ` (${mult}x)` : ''}`,
      kcal: (meal.total_kcal_per_day || 0) / meal.times_per_day * mult,
      protein_g: (meal.total_protein_g_per_day || 0) / meal.times_per_day * mult,
      fat_g: (meal.total_fat_g_per_day || 0) / meal.times_per_day * mult,
      carbs_g: (meal.total_carbs_g_per_day || 0) / meal.times_per_day * mult,
    });
  };

  const saveManual = () => {
    if (!manualDesc.trim()) return;
    doSave({
      meal_type: manualType,
      description: manualDesc.trim(),
      kcal: manualKcal ? Number(manualKcal) : null,
      protein_g: manualProtein ? Number(manualProtein) : null,
      fat_g: manualFat ? Number(manualFat) : null,
      carbs_g: manualCarbs ? Number(manualCarbs) : null,
    });
  };

  if (!open) return null;

  const filteredRecipes = recipes.filter((r) =>
    r.name.toLowerCase().includes(recipeSearch.toLowerCase())
  );

  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    (p.brand || '').toLowerCase().includes(productSearch.toLowerCase())
  );

  const tabs: { key: Tab; label: string }[] = [
    { key: 'recipe', label: 'Rezept' },
    { key: 'product', label: 'Produkt' },
    { key: 'daily', label: 'Daily' },
    { key: 'manual', label: 'Manuell' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col shadow-xl mb-[calc(4.5rem+env(safe-area-inset-bottom,0px))] sm:mb-0">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <h2 className="text-lg font-bold text-gray-900">Essen eintragen</h2>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100">
            <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex px-5 gap-1 border-b border-gray-100 pb-0">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? 'border-emerald-500 text-emerald-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* ===== RECIPE TAB ===== */}
          {tab === 'recipe' && (
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Rezept suchen..."
                value={recipeSearch}
                onChange={(e) => { setRecipeSearch(e.target.value); setSelectedRecipe(null); }}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />

              {selectedRecipe ? (
                <div className="space-y-3">
                  <div className="bg-emerald-50 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-800">{selectedRecipe.name}</p>
                        <p className="text-sm text-gray-500">
                          {selectedRecipe.kcal} kcal &middot; {selectedRecipe.protein_g}g P &middot; {selectedRecipe.fat_g}g F &middot; {selectedRecipe.carbs_g}g K
                        </p>
                      </div>
                      <button onClick={() => setSelectedRecipe(null)} className="text-xs text-gray-400 hover:text-gray-600">Andere</button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Portionen</label>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setRecipeServings((s) => Math.max(0.25, s - 0.25))}
                        className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 font-bold"
                      >-</button>
                      <span className="text-xl font-bold text-gray-900 w-12 text-center">{recipeServings}</span>
                      <button
                        onClick={() => setRecipeServings((s) => s + 0.25)}
                        className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 font-bold"
                      >+</button>
                    </div>
                    {recipeServings !== 1 && selectedRecipe.kcal && (
                      <p className="text-xs text-gray-400 mt-1">
                        = {Math.round(selectedRecipe.kcal * recipeServings)} kcal &middot; {Math.round((selectedRecipe.protein_g || 0) * recipeServings)}g Protein
                      </p>
                    )}
                  </div>
                  <button
                    onClick={saveRecipe}
                    disabled={saving}
                    className="w-full py-3 bg-emerald-500 text-white rounded-xl font-semibold text-sm hover:bg-emerald-600 disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Wird gespeichert...' : 'Eintragen'}
                  </button>
                </div>
              ) : (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {filteredRecipes.slice(0, 50).map((r) => {
                    const isToday = r.source === 'recipe' && todayRecipeIds.has(r.id);
                    return (
                      <button
                        key={`${r.source}-${r.id}`}
                        onClick={() => { setSelectedRecipe(r); setRecipeServings(1); }}
                        className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center gap-3 ${isToday ? 'bg-emerald-50 hover:bg-emerald-100' : 'hover:bg-gray-50'}`}
                      >
                        {r.image_url ? (
                          <img src={r.image_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0 text-lg">
                            {r.source === 'user' ? '👨‍🍳' : '🍽️'}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-medium text-gray-800 truncate">{r.name}</p>
                            {isToday && <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded font-medium flex-shrink-0">Heute</span>}
                          </div>
                          <p className="text-xs text-gray-400">
                            {r.kcal} kcal &middot; {r.protein_g}g P
                            {r.category && <span> &middot; {r.category}</span>}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                  {filteredRecipes.length === 0 && (
                    <p className="text-center text-gray-400 text-sm py-6">Keine Rezepte gefunden</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ===== PRODUCT TAB ===== */}
          {tab === 'product' && (
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Produkt suchen..."
                value={productSearch}
                onChange={(e) => { setProductSearch(e.target.value); setSelectedProduct(null); }}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />

              {selectedProduct ? (
                <div className="space-y-3">
                  <div className="bg-emerald-50 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-800">{selectedProduct.name}</p>
                        {selectedProduct.brand && <p className="text-xs text-gray-500">{selectedProduct.brand}</p>}
                        <p className="text-sm text-gray-500 mt-1">
                          {selectedProduct.kcal_per_100} kcal/100g
                          {selectedProduct.protein_g_per_100 && <span> &middot; {selectedProduct.protein_g_per_100}g P/100g</span>}
                        </p>
                      </div>
                      <button onClick={() => setSelectedProduct(null)} className="text-xs text-gray-400 hover:text-gray-600">Andere</button>
                    </div>
                  </div>
                  {/* Portions / Grams toggle */}
                  <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
                    <button
                      onClick={() => setProductAmountMode('portions')}
                      className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${productAmountMode === 'portions' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500'}`}
                    >Portionen</button>
                    <button
                      onClick={() => setProductAmountMode('grams')}
                      className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${productAmountMode === 'grams' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500'}`}
                    >Gramm</button>
                  </div>
                  {productAmountMode === 'portions' ? (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Portionen ({selectedProduct.portion_size_g || 100}g pro Portion)</label>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setProductServings((s) => Math.max(0.25, s - 0.25))}
                          className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 font-bold"
                        >-</button>
                        <span className="text-xl font-bold text-gray-900 w-12 text-center">{productServings}</span>
                        <button
                          onClick={() => setProductServings((s) => s + 0.25)}
                          className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 font-bold"
                        >+</button>
                      </div>
                      {selectedProduct.kcal_per_100 && (
                        <p className="text-xs text-gray-400 mt-1">
                          = {Math.round((selectedProduct.kcal_per_100 || 0) * (selectedProduct.portion_size_g || 100) / 100 * productServings)} kcal &middot; {Math.round((selectedProduct.protein_g_per_100 || 0) * (selectedProduct.portion_size_g || 100) / 100 * productServings)}g P
                        </p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Menge in Gramm</label>
                      <input
                        type="number"
                        value={productGrams}
                        onChange={(e) => setProductGrams(e.target.value)}
                        placeholder={selectedProduct.portion_size_g ? String(selectedProduct.portion_size_g) : '100'}
                        className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        inputMode="numeric"
                      />
                      {productGrams && selectedProduct.kcal_per_100 && (
                        <p className="text-xs text-gray-400 mt-1">
                          = {Math.round((selectedProduct.kcal_per_100 || 0) / 100 * Number(productGrams))} kcal
                          &middot; {Math.round((selectedProduct.protein_g_per_100 || 0) / 100 * Number(productGrams))}g P
                        </p>
                      )}
                    </div>
                  )}
                  <button
                    onClick={saveProduct}
                    disabled={saving}
                    className="w-full py-3 bg-emerald-500 text-white rounded-xl font-semibold text-sm hover:bg-emerald-600 disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Wird gespeichert...' : 'Eintragen'}
                  </button>
                </div>
              ) : (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {filteredProducts.slice(0, 50).map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { setSelectedProduct(p); setProductServings(1); }}
                      className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <p className="text-sm font-medium text-gray-800">{p.name}</p>
                      <p className="text-xs text-gray-400">
                        {p.brand && <span>{p.brand} &middot; </span>}
                        {p.kcal_per_100} kcal/100g
                      </p>
                    </button>
                  ))}
                  {filteredProducts.length === 0 && (
                    <p className="text-center text-gray-400 text-sm py-6">Keine Produkte gefunden</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ===== DAILY MEAL TAB ===== */}
          {tab === 'daily' && (
            <div className="space-y-2">
              {fixedMeals.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-6">Keine aktiven Daily Meals</p>
              ) : (
                fixedMeals.map((meal) => {
                  const mult = dailyMultiplier[meal.id] ?? 1;
                  return (
                    <div key={meal.id} className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{meal.name}</p>
                          <p className="text-xs text-gray-400">
                            {meal.total_kcal_per_day ? Math.round(meal.total_kcal_per_day / meal.times_per_day) : '?'} kcal pro Portion
                            {meal.total_protein_g_per_day ? ` · ${Math.round(meal.total_protein_g_per_day / meal.times_per_day)}g P` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setDailyMultiplier((m) => ({ ...m, [meal.id]: Math.max(0.5, (m[meal.id] ?? 1) - 0.5) }))}
                            className="w-10 h-10 flex items-center justify-center rounded-lg bg-white text-gray-600 font-bold text-sm border border-gray-200"
                          >-</button>
                          <span className="text-sm font-bold text-gray-900 w-8 text-center">{mult}x</span>
                          <button
                            onClick={() => setDailyMultiplier((m) => ({ ...m, [meal.id]: (m[meal.id] ?? 1) + 0.5 }))}
                            className="w-10 h-10 flex items-center justify-center rounded-lg bg-white text-gray-600 font-bold text-sm border border-gray-200"
                          >+</button>
                        </div>
                        <button
                          onClick={() => saveDailyMeal(meal)}
                          disabled={saving}
                          className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 disabled:opacity-50 transition-colors"
                        >
                          Konsumiert
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* ===== MANUAL TAB ===== */}
          {tab === 'manual' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Beschreibung *</label>
                <input
                  type="text"
                  value={manualDesc}
                  onChange={(e) => setManualDesc(e.target.value)}
                  placeholder="z.B. Haferflocken mit Sojamilch"
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Art</label>
                <div className="flex flex-wrap gap-1.5">
                  {MEAL_TYPES.map((mt) => (
                    <button
                      key={mt.value}
                      onClick={() => setManualType(mt.value)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        manualType === mt.value
                          ? 'bg-emerald-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {mt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">kcal</label>
                  <input type="number" value={manualKcal} onChange={(e) => setManualKcal(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    inputMode="numeric"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Protein (g)</label>
                  <input type="number" value={manualProtein} onChange={(e) => setManualProtein(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    inputMode="numeric"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Fett (g)</label>
                  <input type="number" value={manualFat} onChange={(e) => setManualFat(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    inputMode="numeric"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Kohlenhydrate (g)</label>
                  <input type="number" value={manualCarbs} onChange={(e) => setManualCarbs(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    inputMode="numeric"
                  />
                </div>
              </div>
              <button
                onClick={saveManual}
                disabled={saving || !manualDesc.trim()}
                className="w-full py-3 bg-emerald-500 text-white rounded-xl font-semibold text-sm hover:bg-emerald-600 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Wird gespeichert...' : 'Eintragen'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
